/*
 *   Copyright (c) 2021 vlinder Inc.
 *   All rights reserved.
 *   This software is proprietary of vlinder Inc. and can not be distributed / used for any purpose outside of vlinder Inc. without explicit written consent from the copyright owner.
 */

import React from 'react';
import * as S from './styles';
import * as V from './animation.variants';
import { ToggleAction } from '../ToggleDropDown';
import RenderWhen from '../../When';
import { Row, Col, Container, Stack } from 'react-bootstrap';
import { PlaceHolderComp } from './components/placeholder';

// const DEFAULT_BG_IMAGE = 'https://s3.ap-south-1.amazonaws.com/io.vlinder.widgets/white-bg.jpg';

export interface IIconListProps {
  id: string;
  listImg?: string;
  title?: string | string[];
  meta?: string | string[];
  metaLogo?: string;
  badges?: {
    title?: string;
    key?: string;
    color:
      | 'primary'
      | 'secondary'
      | 'success'
      | 'danger'
      | 'warning'
      | 'info'
      | 'light'
      | 'dark';
  }[];
  [key: string]: any;
}

export interface ISectionListProps {
  title?: string;
  listItems?: IIconListProps[];
  id: string;
  viewAllText?: string;
}

export interface IIconListDataProps {
  data?: IIconListProps[] | ISectionListProps[];
  type?: 'icon' | 'icon-sections';
  isLoading?: boolean;
  metaSymbol?: string;
  titleSymbol?: string;
  onListClick?(data?: any): void;
  onViewAllClick?(key: string): void;
  actionMenu?: {
    key: string;
    label: string;
  }[];
  onActionClick?(key: string, item: any): void;
}

const getFirstLetterOfEachWord = (text: any) =>
  text?.match(/\b\w/g)?.join('')?.substr(0, 2)?.toUpperCase();

export const IconListComp = (props: IIconListDataProps) => {
  const {
    data,
    isLoading,
    actionMenu,
    titleSymbol,
    metaSymbol,
    type,
    onListClick,
    onActionClick,
    onViewAllClick,
  } = props;

  const onListItemClick = (ele: any) => {
    onListClick && onListClick(ele);
  };

  const onToggleMenuClick = (key: string, item: any) => {
    onActionClick && onActionClick(key, item);
  };

  const _getTitle = (title?: string | string[]) => {
    if (Array.isArray(title) && title?.length > 0) {
      return (
        <>
          {title?.map((ele, index) => {
            if (index === title.length - 1) {
              return <span key={`${ele}-${index}`}>{ele}</span>;
            }
            return (
              <span key={`${ele}-${index}`}>
                {ele} {titleSymbol}{' '}
              </span>
            );
          })}
        </>
      );
    } else return title;
  };

  const _getMeta = (title?: string | string[], metaLogo?: string) => {
    if (Array.isArray(title) && title?.length > 0) {
      return (
        <>
          {title?.map((ele, index) => {
            if (index === title.length - 1) {
              return (
                <span key={`${ele}-${index}`}>
                  {index === 0 && !!metaLogo && (
                    <S.MetaLogo
                      src={metaLogo}
                      className="avatar rounded me-1"
                    />
                  )}
                  {ele}
                </span>
              );
            }
            return (
              <span key={`${ele}-${index}`}>
                {index === 0 && !!metaLogo && (
                  <S.MetaLogo src={metaLogo} className="avatar rounded me-1" />
                )}
                {ele} {metaSymbol}{' '}
              </span>
            );
          })}
        </>
      );
    } else return title;
  };

  const _getIconImg = (element: any) => {
    return (
      <S.IconWrapper>
        <S.IconContainer className="avatar align-items-center justify-content-center fw-bold rounded-2 align-self-center me-3">
          <span style={{ color: 'white', transform: 'scale(1)' }}>
            {element?.listImg}
          </span>
        </S.IconContainer>
      </S.IconWrapper>
    );
  };

  const _getBadges = (badges: any) => {
    let _badges: any = [];
    if (Array.isArray(badges) && badges?.length > 0) {
      badges.forEach((ele, index) => {
        _badges.push(
          <S.ButtonComp
            key={`${ele?.key}-${index}`}
            className={`btn rounded-pill btn-sm`}
            color={ele?.color}
          >
            {ele?.title}
          </S.ButtonComp>
        );
      });
      return (
        <Stack direction="horizontal" gap={2}>
          {_badges}
        </Stack>
      );
    }
    return null;
  };

  // @ts-ignore
  const _getDivider = (list: any, index: number) => {
    const isMobile = window.innerWidth < 480;
    if (isMobile && Array.isArray(list) && list?.length - 1 !== index) {
      return <S.HorizontalDivider />;
    }

    if (!isMobile && Array.isArray(list) && list?.length % 3 === 0) {
      if (index < list?.length - 3) {
        return <S.HorizontalDivider />;
      }
    }
    if (!isMobile && Array.isArray(list) && list?.length % 3 === 1) {
      if (index < list?.length - 1) {
        return <S.HorizontalDivider />;
      }
    }
    if (!isMobile && Array.isArray(list) && list?.length % 3 === 2) {
      if (index < list?.length - 2) {
        return <S.HorizontalDivider />;
      }
    }

    return null;
  };

  const _getIconList = (list: any) => {
    let _listItems: any = [];
    if (Array.isArray(list) && list?.length > 0) {
      list?.forEach((element, index) => {
        _listItems.push(
          <Col xs={12} sm={6} md={6} lg={4} key={`${element?.id}-${index}`}>
            <S.ListContainer
              whileHover={'whileHover'}
              transition={V.CardContainerVariant.transition}
              variants={V.CardContainerVariant}
              key={`${element?.id}-${index}`}
              onClick={() => onListItemClick(element?.id)}
            >
              {/*<S.IconImage src={element?.listImg ?? DEFAULT_BG_IMAGE} className="mx-4">*/}
              {_getIconImg(element)}
              {/*</S.IconImage>*/}
              <div>
                <S.Title className="fw-bold">
                  {_getTitle(element.title)}{' '}
                </S.Title>
                <S.Meta className="m-0 p-0">
                  {_getMeta(element?.meta, element?.metaLogo)}
                </S.Meta>
                {_getBadges(element?.badges)}
                <S.HorizontalDivider />
              </div>
              {Array.isArray(actionMenu) && actionMenu?.length > 0 ? (
                <S.ToggleContainer className="toggle-menu">
                  <ToggleAction
                    actionMenu={actionMenu}
                    onActionClick={(key) => onToggleMenuClick(key, element)}
                  />
                </S.ToggleContainer>
              ) : null}
              {/*{_getDivider(list, index)}*/}
            </S.ListContainer>
          </Col>
        );
      });
    }
    return _listItems;
  };

  const _getSectionDivider = (index: number) => {
    if (Array.isArray(data) && data?.length - 1 !== index) {
      return <S.SectionDivider />;
    }
    return null;
  };

  const _getDataList = () => {
    const _dataList: any = [];
    if (isLoading) {
      return <PlaceHolderComp />;
    }
    if (type === 'icon') {
      return <Row>{_getIconList(data)}</Row>;
    }
    if (Array.isArray(data) && data?.length > 0 && type === 'icon-sections') {
      data?.forEach((element, index) => {
        _dataList.push(
          <>
            <Row key={`${element?.title}-${index}`}>
              <Col>
                <RenderWhen>
                  <RenderWhen.If isTrue={!!element?.title}>
                    <S.SectionTitle className="ms-4">
                      {element?.title}{' '}
                      <S.ViewAllContainer className="fw-normal float-end text-secondary">
                        <a
                          onClick={() =>
                            onViewAllClick && onViewAllClick(element?.id)
                          }
                        >
                          {' '}
                          {element?.viewAllText ?? 'See All'}
                        </a>
                      </S.ViewAllContainer>
                    </S.SectionTitle>
                  </RenderWhen.If>
                </RenderWhen>
              </Col>
            </Row>
            <Row>
              {_getIconList(element?.listItems)}
              <Row>
                <Col>{_getSectionDivider(index)}</Col>
              </Row>
            </Row>
          </>
        );
      });
      return _dataList;
    }
    return null;
  };

  return (
    <Container fluid className="m-0 p-0">
      {_getDataList()}
    </Container>
  );
};

IconListComp.defaultProps = {
  metaSymbol: '•',
  titleSymbol: '|',
};
